
import axios from 'axios';
// Filtrar emails institucionales que NO tienen aún un usuario en sys_usuarios
export const filterEmailsWithoutUser = async (emails = []) => {
  try {
    if (!Array.isArray(emails) || emails.length === 0) {
      return [];
    }
    // API_BASE ya está declarado arriba
    const token = localStorage.getItem('authToken');
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
    const res = await axios.post(
      `${API_BASE}/sistema/usuarios/filtrar-no-existentes/`,
      { emails },
      { headers }
    );
    return res.data.emails_sin_usuario || [];
  } catch (err) {
    console.error('[students.js] Error en filterEmailsWithoutUser:', err);
    return [];
  }
};

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';
const API_URL = `${API_BASE}/escolar`;

// Configurar axios para incluir el token de autenticación
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Estudiantes CRUD
export const getStudents = async (params = {}) => {
  const { search, estatus_estudiante, id_campus, id_programa_actual, ciclo_ingreso, id_ciclo_escolar, ordering, page, page_size, no_pagination } = params;
  let url = `${API_URL}/estudiantes/`;
  const query = [];
  if (search) query.push(`search=${encodeURIComponent(search)}`);
  if (estatus_estudiante) query.push(`estatus_estudiante=${encodeURIComponent(estatus_estudiante)}`);
  if (id_campus) query.push(`id_campus=${encodeURIComponent(id_campus)}`);
  if (id_programa_actual) query.push(`id_programa_actual=${encodeURIComponent(id_programa_actual)}`);
  if (id_ciclo_escolar) query.push(`id_ciclo_escolar=${encodeURIComponent(id_ciclo_escolar)}`);
  if (ciclo_ingreso) query.push(`ciclo_ingreso=${encodeURIComponent(ciclo_ingreso)}`);
  if (ordering) query.push(`ordering=${encodeURIComponent(ordering)}`);
  if (page) query.push(`page=${page}`);
  if (page_size) query.push(`page_size=${page_size}`);
  if (no_pagination !== undefined && no_pagination !== null) query.push(`no_pagination=${encodeURIComponent(no_pagination)}`);
  if (query.length) url += `?${query.join('&')}`;
  const res = await axios.get(url);
  if (Array.isArray(res.data)) {
    return res.data;
  }

  if (Array.isArray(res.data?.results)) {
    return res.data.results;
  }

  return [];
};

// Obtener estudiantes con metadata de paginacion (count/results/next/previous)
export const getStudentsPaged = async (params = {}) => {
  const { search, estatus_estudiante, id_campus, id_programa_actual, ciclo_ingreso, id_ciclo_escolar, ordering, page, page_size, no_pagination } = params;
  let url = `${API_URL}/estudiantes/`;
  const query = [];
  if (search) query.push(`search=${encodeURIComponent(search)}`);
  if (estatus_estudiante) query.push(`estatus_estudiante=${encodeURIComponent(estatus_estudiante)}`);
  if (id_campus) query.push(`id_campus=${encodeURIComponent(id_campus)}`);
  if (id_programa_actual) query.push(`id_programa_actual=${encodeURIComponent(id_programa_actual)}`);
  if (id_ciclo_escolar) query.push(`id_ciclo_escolar=${encodeURIComponent(id_ciclo_escolar)}`);
  if (ciclo_ingreso) query.push(`ciclo_ingreso=${encodeURIComponent(ciclo_ingreso)}`);
  if (ordering) query.push(`ordering=${encodeURIComponent(ordering)}`);
  if (page) query.push(`page=${page}`);
  if (page_size) query.push(`page_size=${page_size}`);
  if (no_pagination !== undefined && no_pagination !== null) query.push(`no_pagination=${encodeURIComponent(no_pagination)}`);
  if (query.length) url += `?${query.join('&')}`;

  const res = await axios.get(url);

  if (Array.isArray(res.data)) {
    return {
      count: res.data.length,
      results: res.data,
      page: 1,
      total_pages: 1,
      next: null,
      previous: null,
    };
  }

  if (Array.isArray(res.data?.results)) {
    return {
      ...res.data,
      page: page || 1,
      total_pages: Math.max(1, Math.ceil((res.data.count || 0) / (page_size || res.data.results.length || 1))),
    };
  }

  return {
    count: 0,
    results: [],
    page: page || 1,
    total_pages: 1,
    next: null,
    previous: null,
  };
};

export const getStudent = async (id) => {
  const res = await axios.get(`${API_URL}/estudiantes/${id}/`);
  return res.data;
};

/**
 * Resumen de créditos del estudiante (Asignación de Grupos).
 * Devuelve null si no hay datos disponibles (p. ej. estudiante no ACTIVO o error),
 * para que la UI muestre N/A sin bloquear la pantalla.
 */
export const getStudentCreditsSummary = async (studentId) => {
  try {
    const res = await axios.get(`${API_URL}/estudiantes/${studentId}/resumen-creditos/`);
    return res.data;
  } catch (err) {
    console.warn('[students] No se pudo obtener el resumen de créditos:', err?.response?.status || err?.message);
    return null;
  }
};

/**
 * Obtiene el estudiante asociado al usuario autenticado (por email).
 * Usado por el Portal de Estudiantes para cargar datos del alumno logueado.
 */
export const getStudentMe = async () => {
  const res = await axios.get(`${API_URL}/estudiantes/me/`);
  return res.data;
};

export const createStudent = async (data) => {
  // Asegurarse que id_institucion e id_campus estén presentes
  if (!data.id_institucion && data.id_institucion !== 0) {
    throw new Error('id_institucion es requerido para crear el estudiante');
  }
  if (!data.id_campus && data.id_campus !== 0) {
    throw new Error('id_campus es requerido para crear el estudiante');
  }
  const res = await axios.post(`${API_URL}/estudiantes/`, data);
  return res.data;
};

export const updateStudent = async (id, data) => {
  const res = await axios.put(`${API_URL}/estudiantes/${id}/`, data);
  return res.data;
};

export const patchStudent = async (id, data) => {
  const res = await axios.patch(`${API_URL}/estudiantes/${id}/`, data);
  return res.data;
};

export const deleteStudent = async (id) => {
  const res = await axios.delete(`${API_URL}/estudiantes/${id}/`);
  return res.data;
};

// Obtener lista de programas académicos para dropdown
export const getProgramasDropdown = async () => {
  const res = await axios.get(`${API_URL}/programas-academicos/`);
  const raw = Array.isArray(res.data)
    ? res.data
    : (Array.isArray(res.data?.results) ? res.data.results : []);

  return raw
    .map((p) => ({
      id_programa: p?.id_programa ?? p?.id ?? null,
      nombre_programa: p?.nombre_programa || p?.name || p?.nombre || '',
    }))
    .filter((p) => p.id_programa != null && p.nombre_programa);
};

// Obtener detalle completo del estudiante (usa el retrieve estándar del ViewSet)
export const getStudentDetailComplete = async (id) => {
  const res = await axios.get(`${API_URL}/estudiantes/${id}/`);
  return res.data;
};

// Subir documento al expediente del estudiante
export const uploadStudentDocument = async (id, formData) => {
  const res = await axios.post(`${API_URL}/estudiantes/${id}/subir-documento/`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return res.data;
};

// Obtener estado financiero del estudiante (desde API financiero)
export const getStudentFinancialStatus = async (id) => {
  const API_FINANCIERO = `${process.env.REACT_APP_API_BASE || 'http://localhost:8000/api'}/financiero`;
  const res = await axios.get(`${API_FINANCIERO}/portal-estudiante/${id}/estado-cuenta/`);
  return res.data;
};

// ─── Documentos de Expediente del Estudiante ──────────────────────────────────

const DOC_URL = `${API_BASE}/documental`;

/**
 * Obtener documentos activos de un estudiante.
 */
export const getDocumentosEstudiante = async (estudianteId) => {
  const res = await axios.get(`${DOC_URL}/expedientes-estudiantes/?id_estudiante=${estudianteId}`);
  return res.data;
};

/**
 * Subir documento PDF para un estudiante.
 * @param {number} estudianteId
 * @param {string} tipoDocumento - ACTA_NACIMIENTO | CURP | COMPROBANTE_DOMICILIO | INE | CONSTANCIA_ESTUDIOS
 * @param {File} archivo - Archivo PDF
 */
export const subirDocumentoEstudiante = async (estudianteId, tipoDocumento, archivo) => {
  const formData = new FormData();
  formData.append('id_estudiante', estudianteId);
  formData.append('tipo_documento', tipoDocumento);
  formData.append('archivo', archivo);
  formData.append('nombre_archivo', archivo.name);
  const res = await axios.post(`${DOC_URL}/expedientes-estudiantes/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
};

/**
 * Eliminar (soft-delete) un documento de estudiante.
 */
export const eliminarDocumentoEstudiante = async (documentoId) => {
  const res = await axios.delete(`${DOC_URL}/expedientes-estudiantes/${documentoId}/`);
  return res.data;
};

/**
 * Obtener URL de descarga de un documento de estudiante.
 */
export const getUrlDescargaDocEstudiante = (documentoId) => {
  return `${DOC_URL}/expedientes-estudiantes/${documentoId}/descargar/`;
};

/**
 * Subir fotografía de perfil del estudiante (JPG/JPEG/PNG, máx 5MB).
 * Reutiliza el endpoint de expediente con tipo_documento='FOTO_PERFIL'.
 * Si ya existe foto activa, el backend la marca REEMPLAZADO y elimina archivo previo.
 */
export const subirFotoEstudiante = async (estudianteId, archivo) => {
  const formData = new FormData();
  formData.append('id_estudiante', estudianteId);
  formData.append('tipo_documento', 'FOTO_PERFIL');
  formData.append('archivo', archivo);
  formData.append('nombre_archivo', archivo.name);
  const res = await axios.post(`${DOC_URL}/expedientes-estudiantes/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
};

// ─── Bajas / Reactivaciones ───────────────────────────────────────────────────

export const getBajasEstudiante = async (idEstudiante) => {
  const res = await axios.get(`${API_URL}/bajas/?id_estudiante=${idEstudiante}`);
  if (Array.isArray(res.data)) return res.data;
  return res.data?.results || [];
};

export const crearBajaEstudiante = async (data) => {
  const res = await axios.post(`${API_URL}/bajas/`, data);
  return res.data;
};

export const reactivarBajaEstudiante = async (idBaja, data) => {
  const res = await axios.patch(`${API_URL}/bajas/${idBaja}/`, { ...data, reactivar: true });
  return res.data;
};
